import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { setSamlModeBypass } from '../../../../api/firebase/function/security/securitySamlFunction';
import { SAML_MODE_BYPASS_TOGGLE_ERROR } from '../../../../constants/labels';
import {
  gray40,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { dialogText, dialogTitle } from '../../../../constants/location';

interface Props {
  open: boolean;
  companyId: string;
  /** 変更前のバイパス状態。ダイアログはこの反対の状態へ切り替える前提で開く。 */
  currentBypass: boolean;
  onClose: () => void;
  /** 切替成功時に新しいバイパス状態を返す。 */
  onChanged: (afterBypass: boolean) => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bodyText: {
    ...dialogText,
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

/**
 * 運営 admin 用: 企業の SAML 必須モード 一時バイパス（saml_mode_bypass）切替ダイアログ（#2983）。
 * IdP 設定ミス等で companyAdmin がログイン不能になった際の緊急救済経路。
 * バイパス中は SAML 必須テナントでも従来ログイン（メール/パスワード）を許可する。
 * IP 制限の一時無効化トグルと同パターンで、企業詳細「概要」タブの行から開く。
 */
const SamlModeBypassToggleDialog: React.FC<Props> = ({
  open,
  companyId,
  currentBypass,
  onClose,
  onChanged,
}) => {
  const pendingBypass = !currentBypass;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      await setSamlModeBypass({
        company_id: companyId,
        bypass: pendingBypass,
      });
      onChanged(pendingBypass);
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : SAML_MODE_BYPASS_TOGGLE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          SAML必須モードを{pendingBypass ? '一時バイパスしますか？' : '再適用しますか？'}
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.fieldWrapper}>
        {error && <Box sx={styles.errorBox}>{error}</Box>}

        <Typography sx={styles.bodyText}>
          {pendingBypass
            ? 'この企業の SAML 必須モードを一時的にバイパスし、従来ログイン（メール/パスワード）を許可します。IdP 設定ミスによるロックアウトの救済用です。復旧後は必ず再適用してください。'
            : 'バイパスを解除し、SAML 必須モード（SSO 以外のログイン禁止）を再適用します。'}
        </Typography>

        <Box sx={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            {pendingBypass ? 'バイパスする' : '再適用する'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SamlModeBypassToggleDialog;
